import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  buildVendorTripRows,
  buildVendorMonthsForYear,
  rowsForMonth,
  rowsWithoutMonth,
  vendorPayoutStatus,
  formatVendorShare,
  VENDOR_PAYOUT_LEGEND_ADMIN,
} from '../../utils/vendorPayoutStatus';
import './BookingCalendar.scss';

function formatDate(iso) {
  if (!iso) return '—';
  return moment(iso).format('D MMM YYYY');
}

export default function VendorTripsPanel({
  assignments,
  onViewMembers,
  onRecordPayment,
  payingTripId,
  loading,
}) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const allRows = useMemo(() => buildVendorTripRows(assignments), [assignments]);
  const months = useMemo(() => buildVendorMonthsForYear(allRows, year, { audience: 'admin' }), [allRows, year]);
  const unscheduled = useMemo(() => rowsWithoutMonth(allRows), [allRows]);

  const selectedYearMonth = selectedMonth ? `${year}-${String(selectedMonth).padStart(2, '0')}` : null;
  const displayRows = useMemo(() => {
    if (!selectedYearMonth) return [];
    return rowsForMonth(allRows, selectedYearMonth);
  }, [allRows, selectedYearMonth]);

  if (loading) {
    return <div className="p-3 text-muted small">Loading trips…</div>;
  }

  if (allRows.length === 0) {
    return <div className="p-3 text-muted small">No trips assigned to this vendor.</div>;
  }

  const monthMeta = months.find((m) => m.month === selectedMonth);
  const monthLabel = selectedMonth
    ? moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')
    : null;

  return (
    <div className="vendor-trips-panel p-3 booking-cal">
      <p className="text-muted small mb-2 mb-md-3">
        Click a month → <strong>Record payment</strong> confirms the trip and pays the vendor in one step.
      </p>

      <div className="booking-cal__legend mb-2">
        {VENDOR_PAYOUT_LEGEND_ADMIN.map((row) => (
          <span key={row.label}>
            <span className={`badge badge-${row.badge}`}>{row.label}</span> {row.desc.charAt(0).toLowerCase() + row.desc.slice(1)}
          </span>
        ))}
      </div>

      <div className="d-flex justify-content-end mb-2">
        <div className="booking-cal__year-nav">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setYear((y) => y - 1); setSelectedMonth(null); }} aria-label="Previous year">‹</button>
          <span className="booking-cal__year-label">{year}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setYear((y) => y + 1); setSelectedMonth(null); }} aria-label="Next year">›</button>
        </div>
      </div>

      <div className="booking-cal__grid" role="list">
        {months.map((m) => {
          const isSelected = selectedMonth === m.month;
          const isCurrentMonth = year === currentYear && m.month === new Date().getMonth() + 1;
          const detailParts = [];
          if (m.paid > 0) detailParts.push(`${m.paid} paid`);
          if (m.unpaid > 0) detailParts.push(`${m.unpaid} to pay`);
          return (
            <button
              key={m.yearMonth}
              type="button"
              role="listitem"
              className={`booking-cal__cell${isSelected ? ' booking-cal__cell--selected' : ''}${isCurrentMonth ? ' booking-cal__cell--current' : ''}`}
              onClick={() => setSelectedMonth((prev) => (prev === m.month ? null : m.month))}
              aria-pressed={isSelected}
            >
              <span className="booking-cal__cell-label">{m.label}</span>
              <span className="booking-cal__month-line-total d-block text-center">{m.total}</span>
              {m.total > 0 && detailParts.length > 0 && (
                <span className="booking-cal__month-line-detail d-block text-center">
                  {detailParts.join(' · ')}
                </span>
              )}
              {m.unpaid > 0 && (
                <span className="booking-cal__cell-attention">{m.unpaid} to pay</span>
              )}
            </button>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <p className="text-muted small mt-2 mb-0">{unscheduled.length} trip{unscheduled.length !== 1 ? 's' : ''} with no date — open from trip list above.</p>
      )}

      {selectedMonth && monthMeta && (
        <section className="booking-cal__detail mt-3">
          <h4 className="h6 mb-3">{monthLabel} — {displayRows.length} trip{displayRows.length !== 1 ? 's' : ''}</h4>

          {displayRows.length === 0 ? (
            <p className="text-muted mb-0">No trips this month.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover booking-cal__table mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Trip</th>
                    <th>Date</th>
                    <th>Vendor share</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, i) => {
                    const status = vendorPayoutStatus(row.item, { audience: 'admin' });
                    const key = `${row.type}-${row.trip.assignment_idx}-${i}`;
                    const paying = payingTripId === key;
                    const typeLabel = row.type === 'Group' ? 'Group Tour' : row.type === 'Shared' ? 'Shared Rental' : 'Private Tour';

                    return (
                      <tr key={key}>
                        <td>{row.type}</td>
                        <td>{row.link ? <Link to={row.link}>{row.name}</Link> : row.name}</td>
                        <td>{formatDate(row.date)}</td>
                        <td>{formatVendorShare(row.item)}</td>
                        <td>
                          <span className={`badge badge-${status.badge}`}>{status.label}</span>
                          {status.hint && <div className="small text-muted mt-1">{status.hint}</div>}
                        </td>
                        <td className="text-right text-nowrap">
                          {row.canViewMembers && onViewMembers && (
                            <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => onViewMembers({ ...row, type: typeLabel })}>
                              Members
                            </button>
                          )}
                          {status.key === 'ready_to_pay' && onRecordPayment && (
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => onRecordPayment(row, key)}
                              disabled={!!payingTripId}
                            >
                              {paying ? 'Recording…' : 'Record payment'}
                            </button>
                          )}
                          {!row.canAdminMark && row.link && status.key === 'ready_to_pay' && (
                            <Link to={row.link} className="btn btn-sm btn-outline-warning ml-1">Assign vendor</Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {!selectedMonth && (
        <p className="text-muted text-center small mt-3 mb-0">Click a month to see trips.</p>
      )}
    </div>
  );
}
